import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PageHeader from "../../components/pageHeader";
import { useToast } from "../../hooks/useToast";
import ConfirmDeleteModal from "../../components/confirmDeleteModal";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";
import { useSelector, useDispatch } from "react-redux";
import { API_BASE } from "../../config/apiConfig";
import { FaEdit, FaTrash } from "react-icons/fa";
import { resetActivities } from "../../features/activities/activitiesSlice";

export default function ActivityDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = sessionStorage.getItem("access_token");
  const user = useSelector((state) => state.user.user);

  const location = useLocation();
  const from = location.state?.from;
  const coachId = location.state?.coachId;

  const isAdmin = Array.isArray(user.role)
    ? user.role.includes("admin")
    : user.role === "admin";

  const { showToast, ToastContainer } = useToast();

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  // ------------------------------------------------------------
  // LOAD DETAIL FROM BACKEND
  // ------------------------------------------------------------
  useEffect(() => {
    async function fetchDetail() {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/activities-detail/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (data.success) {
          setActivity(data.activity);
        } else {
          showToast("Failed to load activity", "error");
        }
      } finally {
        setLoading(false);
      }
    }

    fetchDetail();
  }, [id, token]);

  // ------------------------------------------------------------
  // DELETE (calls backend delete API)
  // ------------------------------------------------------------
  function handleDelete() {
    setDeleteId(id);
  }

  async function confirmDelete() {
    if (!deleteId) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/activities-delete/${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        dispatch(resetActivities());
        if (from === "other-coach-side") {
          navigate(`/other-coaches-activities-list/${coachId}`);
        } else {
          navigate("/manage-activities");
        }
      } else {
        showToast(data.message || "Delete failed", "error");
      }
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  }

  if (!activity) return <LoadingOverlay show={true} />;

  let backTo = "/manage-activities";

  if (from === "coach-side") {
    backTo = "/coach-activities"
  } else if (from === "other-coach-side") {
    backTo = `/other-coaches-activities-list/${coachId}`
  } else if (from === "player-side") {
    backTo = "/player-activities"
  } else {
    backTo = "/manage-activities"
  };

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------
  return (
    <MainContentLayout>
      <LoadingOverlay show={loading} />
      <PageHeader title="Activity Detail" backTo={backTo} />

      {isAdmin && (
        <div className="flex gap-3 justify-end px-3">
          {/* Edit */}
          <button
            onClick={() => {
              if (from === "other-coach-side") {
                navigate(`/activities/edit/${id}`, { state: { from: "other-coach-side", coachId: coachId }});
              } else {
                navigate(`/activities/edit/${id}`);
              }
            }}
            className="text-blue-600 hover:text-blue-800"
            title="Edit Activity"
          >
            <FaEdit size={18} />
          </button>

          {/* Delete */}
          <button
            onClick={handleDelete}
            className="text-red-600 hover:text-red-800"
            title="Delete Activity"
          >
            <FaTrash size={18} />
          </button>
        </div>
      )}

      <div className="p-5 mt-4 max-w-2xl mx-auto w-full text-left">
        <div className="grid grid-cols-2 gap-y-2">
          <span className="font-semibold">Name:</span>
          <span className="hyphens-auto">{activity.name}</span>

          <span className="font-semibold">Type:</span>
          <span>{activity.type}</span>

          {activity.type === "tournament" && (
            <>
              <span className="font-semibold">Ranking Type:</span>
              <span>{activity.rankingType || "-"}</span>
            </>
          )}

          <span className="font-semibold">Start Date:</span>
          <span>{activity.startDate}</span>

          <span className="font-semibold">Start Time:</span>
          <span>{activity.startTime || "-"}</span>

          <span className="font-semibold">End Date:</span>
          <span>{activity.endDate}</span>

          <span className="font-semibold">End Time:</span>
          <span>{activity.endTime || "-"}</span>

          <span className="font-semibold">Remarks:</span>
          <span className="hyphens-auto">
            {activity.remarks || "-"}
          </span>

          <span className="font-semibold">Status:</span>
          <span className="font-medium capitalize">{activity.status}</span>

          <span className="font-semibold">Availability:</span>
          <span
            className={`font-medium capitalize
              ${activity.visibility === "public" 
                ? "text-blue-700" 
                : "text-purple-700"
              }`}
          >{activity.visibility}</span>
        </div>
      </div>

      {/* Delete modal */}
      {deleteId && (
        <ConfirmDeleteModal
          show={true}
          title="Delete Activity"
          message="Are you sure you want to delete this activity?"
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
        />
      )}

      <ToastContainer />
    </MainContentLayout>
  );
}
